import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

/**
 * The client's live compliance register.
 *
 * This is the lock-in: the WHS officer comes to depend on it, and switching operators means
 * losing the audit trail. Free at every tier, branded to the operator, never to TagVault.
 *
 * Wording rule (enforced by scripts/copy-check.mjs): this page presents records that SUPPORT
 * an AS/NZS 3760 program. It never asserts that anyone or anything is compliant.
 */

type Asset = {
  tagId: string;
  description: string;
  location: string;
  status: string;
  lastTestedAt: number | null;
  nextDueAt: number | null;
};

type RegisterData = {
  operator: { tradingName: string; brandColor: string | null; timezone: string };
  client: { name: string };
  sites: Array<{ site: { id: string; name: string; address: string }; assets: Asset[] }>;
};

/**
 * Checked at build time by scripts/web-env-check.mjs — Vite inlines this, so a missing
 * value cannot be fixed after the bundle is built. Never throw on it at module scope:
 * that lets the minifier discard the whole app as dead code (see scripts/assert-bundle.mjs).
 */
const SITE_URL = import.meta.env.VITE_CONVEX_SITE_URL;

function fmt(ms: number | null, tz: string): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-AU', { timeZone: tz });
}

function dueState(nextDueAt: number | null): 'overdue' | 'soon' | 'ok' | 'none' {
  if (!nextDueAt) return 'none';
  const days = (nextDueAt - Date.now()) / 86_400_000;
  if (days < 0) return 'overdue';
  if (days < 30) return 'soon';
  return 'ok';
}

export default function Register() {
  const { slug, token } = useParams();
  const [data, setData] = useState<RegisterData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!SITE_URL) {
        setError('This portal is not configured. Please tell the operator who sent you this link.');
        return;
      }
      try {
        const response = await fetch(
          `${SITE_URL}/portal/register?slug=${encodeURIComponent(slug ?? '')}&token=${encodeURIComponent(token ?? '')}`,
        );
        if (!response.ok) throw new Error('unavailable');
        const body = (await response.json()) as RegisterData;
        if (!cancelled) setData(body);
      } catch {
        if (!cancelled) setError('This link is not valid. Ask your test & tag operator for a new one.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, token]);

  const totals = useMemo(() => {
    if (!data) return null;
    const assets = data.sites.flatMap((s) => s.assets);
    return {
      total: assets.length,
      overdue: assets.filter((a) => dueState(a.nextDueAt) === 'overdue').length,
      soon: assets.filter((a) => dueState(a.nextDueAt) === 'soon').length,
      outOfService: assets.filter((a) => a.status === 'out_of_service').length,
    };
  }, [data]);

  if (error) return <main className="wrap"><p className="error">{error}</p></main>;
  if (!data || !totals) return <main className="wrap"><p className="muted">Loading register…</p></main>;

  const accent = data.operator.brandColor ?? '#0f766e';

  return (
    <main className="wrap" style={{ ['--accent' as string]: accent }}>
      <header className="head">
        <p className="operator">{data.operator.tradingName}</p>
        <h1>{data.client.name}</h1>
        <p className="muted">Asset register and test history</p>
      </header>

      <section className="tiles">
        <div className="tile"><span className="n">{totals.total}</span><span className="l">items</span></div>
        <div className={`tile${totals.overdue ? ' alert' : ''}`}>
          <span className="n">{totals.overdue}</span><span className="l">overdue</span>
        </div>
        <div className="tile"><span className="n">{totals.soon}</span><span className="l">due in 30 days</span></div>
        <div className={`tile${totals.outOfService ? ' alert' : ''}`}>
          <span className="n">{totals.outOfService}</span><span className="l">out of service</span>
        </div>
      </section>

      {data.sites.map(({ site, assets }) => (
        <section key={site.id} className="site">
          <h2>{site.name}</h2>
          <p className="muted">{site.address}</p>
          <table>
            <thead>
              <tr><th>Tag</th><th>Item</th><th>Location</th><th>Last tested</th><th>Next due</th></tr>
            </thead>
            <tbody>
              {assets.map((a) => (
                <tr key={a.tagId} className={a.status === 'out_of_service' ? 'oos' : ''}>
                  <td className="tag">{a.tagId}</td>
                  <td>
                    {a.description}
                    {a.status === 'out_of_service' ? <span className="badge">out of service</span> : null}
                  </td>
                  <td className="muted">{a.location || '—'}</td>
                  <td>{fmt(a.lastTestedAt, data.operator.timezone)}</td>
                  <td className={dueState(a.nextDueAt)}>{fmt(a.nextDueAt, data.operator.timezone)}</td>
                </tr>
              ))}
              {assets.length === 0 ? (
                <tr><td colSpan={5} className="muted">No items registered at this site yet.</td></tr>
              ) : null}
            </tbody>
          </table>
        </section>
      ))}

      <footer>
        <p className="muted small">
          These records support your AS/NZS 3760 program. They do not certify compliance with any
          legal obligation, and requirements vary by state and territory. Maintained by{' '}
          {data.operator.tradingName}.
        </p>
      </footer>
    </main>
  );
}
